import { ReactNode, createContext, useCallback, useContext } from 'react';
import {
  ExerciseDetailsByDate,
  MealDetailsByDate,
  SleepDetailsByDate,
  SleepStage,
  MetricHRSummary,
} from '@demind-inc/core';
import { useRecoilValue } from 'recoil';
import { Dayjs } from 'dayjs';

import { useAuthContext } from './AuthProvider';
import { useExerciseDetails, useMealDetails, useSleepDetails } from '../queries';
import { selectedDateAtom } from '../recoil';
import { TotalSleepHourAndMinute, translateSecondsToHourAndMinutes } from '../../helpers';
import { isEmpty } from 'lodash';
import { getWeekDatesOf } from '../helpers';

interface IMetricsContext {
  sleepDetails: SleepDetailsByDate[];
  exerciseDetails: ExerciseDetailsByDate[];
  getSleepOnDate: (date: Dayjs, fromCache?: boolean) => GetSleepOnDateRes;
  isFetchingSleep: boolean;
  isFetchingExercise: boolean;
  isFetchingMeal: boolean;
  mealDetails: MealDetailsByDate[];
  getMealOnDate: (date: Dayjs) => MealDetailsByDate | undefined;
}
export interface GetSleepOnDateRes {
  totalSleepHours: TotalSleepHourAndMinute;
  hasNoSleepData: boolean;
  sleepStage?: SleepStage;
  awakeTime?: string;
  sleepTime?: string;
  sleepSource?: string;
  sleepDurationMin?: number;
  sleepEfficiency?: number;
  heartRateSummary?: MetricHRSummary;
  avgWakeTimeHHmm?: string;
  avgSleepTimeHHmm?: string;
}

export const MetricsContext = createContext({} as IMetricsContext);
export const useMetricsContext = () => useContext(MetricsContext);

interface MetricsProviderProps {
  children: ReactNode;
  syncDates?: Dayjs[];
}

const formatSleepSource = (source?: string) => {
  if (!source) {
    return source;
  }

  return source.toLowerCase().includes('garmin') ? 'Garmin devices' : source;
};

export const MetricsProvider = ({ children, syncDates }: MetricsProviderProps) => {
  const { user } = useAuthContext();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const weekDates = getWeekDatesOf(selectedDate);
  const { sleepDetails, isLoading: isFetchingSleep } = useSleepDetails({
    userId: user.userId,
    metricId: user.metricId!,
    dates: (syncDates ?? weekDates).map((date) => date.format('YYYY-MM-DD')),
  });
  const { exerciseDetails = [], isLoading: isFetchingExercise } = useExerciseDetails({
    metricId: user.metricId!,
    dates: syncDates
      ? syncDates.map((date) => date.format('YYYY-MM-DD'))
      : [selectedDate.format('YYYY-MM-DD')],
  });
  const { mealDetails = [], isLoading: isFetchingMeal } = useMealDetails({
    metricId: user.metricId!,
    dates: syncDates
      ? syncDates.map((date) => date.format('YYYY-MM-DD'))
      : [selectedDate.format('YYYY-MM-DD')],
  });
  const normalizedMealDetails = Array.isArray(mealDetails) ? mealDetails : [];

  const getSleepOnDate = useCallback(
    (date: Dayjs) => {
      const targetSleep = Array.isArray(sleepDetails)
        ? sleepDetails.find((item) => item.date === date.format('YYYY-MM-DD'))
        : undefined;
      const sourceData = targetSleep?.details;
      const totalSleepHours = translateSecondsToHourAndMinutes((sourceData?.durationMin ?? 0) * 60);
      const hasNoSleepData = isFetchingSleep ? false : !sourceData || isEmpty(sourceData);
      const sleepStage = sourceData?.stages;
      const awakeTime = sourceData?.awakeTime;
      const sleepTime = sourceData?.sleepTime;
      const sleepSource = formatSleepSource(sourceData?.source);
      const sleepDurationMin = sourceData?.durationMin;
      const sleepEfficiency = sourceData?.efficiency;
      const heartRateSummary = sourceData?.heartRateSummary;
      const predictedSleep = targetSleep?.predictedSleep;

      return {
        sleepStage,
        awakeTime,
        sleepTime,
        sleepSource,
        sleepDurationMin,
        totalSleepHours,
        sleepEfficiency,
        heartRateSummary,
        hasNoSleepData,
        avgWakeTimeHHmm: predictedSleep?.awakeTime,
        avgSleepTimeHHmm: predictedSleep?.sleepTime,
      };
    },
    [sleepDetails, isFetchingSleep]
  );

  const getMealOnDate = useCallback(
    (date: Dayjs) => {
      const targetMeal = normalizedMealDetails.find(
        (item) => item.date === date.format('YYYY-MM-DD')
      );
      return targetMeal;
    },
    [normalizedMealDetails]
  );

  return (
    <MetricsContext.Provider
      value={{
        exerciseDetails,
        mealDetails: normalizedMealDetails,
        sleepDetails,
        isFetchingSleep,
        isFetchingExercise,
        isFetchingMeal,
        getSleepOnDate,
        getMealOnDate,
      }}
    >
      {children}
    </MetricsContext.Provider>
  );
};
